import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { createTheme } from '@mui/material/styles';

import { siteIds } from '../../constants';
import { siteId } from '../../sites';

import * as instrumentariumBrandStyles from './instrumentarium/brandStyles';
import * as nissenBrandStyles from './nissen/brandStyles';
import * as keopsBrandStyles from './keops/brandStyles';
import * as terveystaloBrandStyles from './terveystalo/brandStyles';

let brandStyles = {};

if (isEqual(siteId, siteIds.Nissen)) {
  brandStyles = nissenBrandStyles;
} else if (isEqual(siteId, siteIds.Keops)) {
  brandStyles = keopsBrandStyles;
} else if (isEqual(siteId, siteIds.Terveystalo)) {
  brandStyles = terveystaloBrandStyles;
} else {
  brandStyles = instrumentariumBrandStyles;
}

const baseTheme = createTheme();
const theme = createTheme({
  ...baseTheme,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1332,
      xl: 1920,
    },
  },
  typography: {
    fontFamily: brandStyles.typography.fontFamilyBody,
    fontSize: 12,
    em: {
      fontFamily: brandStyles.typography.fontFamilyBody,
      fontWeight: 'bold',
    },
    strong: {
      fontFamily: brandStyles.typography.fontFamilyBody,
      fontWeight: 'bold',
    },
    body1: {
      fontSize: brandStyles.typography.mobile.fontSizeBody1,
    },
  },
  palette: {
    primary: {
      light: brandStyles.colors.palette.primary.light,
      main: brandStyles.colors.palette.primary.main,
      dark: brandStyles.colors.palette.primary.dark,
      contrastText: brandStyles.colors.palette.primary.contrastText,
    },
    secondary: {
      light: brandStyles.colors.palette.secondary.light,
      main: brandStyles.colors.palette.secondary.main,
      dark: brandStyles.colors.palette.secondary.dark,
      contrastText: brandStyles.colors.palette.secondary.contrastText,
    },
    background: {
      paper: brandStyles.colors.palette.background.paper,
    },
    border: {
      color: '#D2D8DB',
    },
    status: {
      error: 'rgb(254, 34, 19)',
    },
  },
  box: {
    background: '#EAF2F6',
    borderRadius: '4px',
  },
  footer: {
    backgroundColor: brandStyles.components.footer.backgroundColor,
  },
  ssnButton: {
    padding: `calc(6px + ${(get(brandStyles), ['typography', 'padding', 'top'], ' 0px')}) 7px 6px`,
  },
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&.MuiPaper-elevation1': {
            boxShadow: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        content: {
          margin: '16px 0',
          '&.Mui-expanded': {
            margin: '16px 0',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          boxShadow: 'none',
          borderBottom: '1px solid rgba(205, 205, 205, 1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          fontSize: 12,
          fontFamily: brandStyles.typography.fontFamilyBody,
          fontWeight: 'bold',
          padding: '13px 16px 10px 16px',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        label: {
          alignItems: 'initial !important',
        },
        contained: {
          backgroundColor: brandStyles.colors.button.contained.backgroundColor,
          border: `1px solid ${brandStyles.colors.button.contained.backgroundColor}`,
          color: brandStyles.colors.button.contained.color,
          '&:hover': {
            backgroundColor: `${brandStyles.colors.button.contained.hover.backgroundColor} !important`,
            borderColor: brandStyles.colors.button.contained.hover.borderColor,
            color: brandStyles.colors.button.contained.hover.color,
          },
        },
        outlined: {
          border: `1px solid ${brandStyles.colors.palette.primary.main} !important`,
          color: brandStyles.colors.palette.primary.main,
          padding: '13px 16px 10px 16px',
          '&:hover': {
            backgroundColor: brandStyles.colors.button.outlined.hover.backgroundColor,
            color: brandStyles.colors.button.outlined.hover.color,
          },
        },
        outlinedSecondary: {
          '&:hover': {
            backgroundColor: `${brandStyles.colors.button.outlined.hover.backgroundColor} !important`,
          },
        },
        containedSecondary: {
          backgroundColor: brandStyles.colors.button.contained.backgroundColor,
          border: `1px solid ${brandStyles.colors.button.contained.backgroundColor}`,
          color: brandStyles.colors.button.contained.color,
          '&:hover': {
            backgroundColor: `${brandStyles.colors.button.contained.hover.backgroundColor} !important`,
            borderColor: brandStyles.colors.button.contained.hover.borderColor,
            color: brandStyles.colors.button.contained.hover.color,
          },
        },
        sizeLarge: {
          padding: '13px 22px',
        },
        startIcon: {
          alignItems: 'center',
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '& >.MuiIcon-root': {
            alignItems: 'center',
            display: 'inline-flex',
          },
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          margin: 0,
          minHeight: '465px',
          padding: '16px',
          width: '100%',
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        header: {
          justifyContent: 'space-between',
        },
        slideTransition: {
          minHeight: '335px',
        },
        weekContainer: {
          justifyContent: 'space-between',
        },
        weekDayLabel: {
          margin: '6px',
          width: '40px',
        },
      },
    },
    MuiExpansionPanelSummary: {
      styleOverrides: {
        content: {
          margin: '30px 0 !important',
        },
        root: {
          padding: '0 15px 0 15px',
          [baseTheme.breakpoints.up('md')]: {
            padding: '0 30px 0 30px',
          },
        },
      },
    },

    MuiExpansionPanelDetails: {
      styleOverrides: {
        root: {
          padding: '0 15px 15px 15px',
          [baseTheme.breakpoints.up('md')]: {
            padding: '0 30px 30px 30px',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 16,
        },
      },
    },
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        button: {
          border: '1px solid',
          borderColor: brandStyles.colors.palette.primary.main,
          borderRadius: '4px',
          color: brandStyles.colors.palette.primary.main,
          height: '38px',
          marginLeft: 8,
          width: '38px',
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          paddingLeft: 0,
          paddingRight: 0,
        },
        label: {
          fontSize: '1rem',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontSize: 28,
          color: brandStyles.colors.palette.primary.main,
          fontFamily: brandStyles.typography.fontFamilyTitle,
          fontWeight: 'bold',
          lineHeight: 1,
          paddingTop: '30px',
          paddingBottom: '30px',
          textAlign: 'left',
          [baseTheme.breakpoints.up('md')]: {
            fontSize: 40,
            lineHeight: '64px',
          },
        },
        h2: {
          fontSize: 24,
          fontFamily: brandStyles.typography.fontFamilyTitle,
          fontWeight: 'bold',
          lineHeight: 1.15,
        },
        h3: {
          fontSize: 18,
          fontFamily: brandStyles.typography.fontFamilyTitle,
          fontWeight: 'bold',
          lineHeight: 1,
        },
        h4: {
          fontSize: 16,
          fontFamily: brandStyles.typography.fontFamilyTitle,
          fontWeight: 'bold',
          lineHeight: 1,
          marginBottom: 20,
        },
        h5: {
          fontSize: 16,
          fontFamily: brandStyles.typography.fontFamilyTitle,
          fontWeight: 'bold',
          lineHeight: 1,
          marginBottom: 16,
        },
        h6: {
          fontSize: 14,
          fontFamily: brandStyles.typography.fontFamilyTitle,
          fontWeight: 'bold',
          lineHeight: '18px',
          [baseTheme.breakpoints.up('md')]: {
            fontSize: 18,
            lineHeight: '24px',
          },
        },
        body1: {
          fontSize: brandStyles.typography.mobile.fontSizeBody1,
          width: '100%',
          lineHeight: 1.45,
          [baseTheme.breakpoints.up('md')]: {
            fontSize: brandStyles.typography.desktop.fontSizeBody1,
          },
        },
        body2: {
          fontSize: brandStyles.typography.mobile.fontSizeBody2,
          width: '100%',
          lineHeight: 1.5,
          [baseTheme.breakpoints.up('md')]: {
            fontSize: brandStyles.typography.desktop.fontSizeBody2,
          },
        },
      },
    },
    MuiPickersCalendar: {
      styleOverrides: {
        transitionContainer: {
          marginBottom: 12,
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          height: '40px',
          margin: '6px',
          width: '40px',
          padding: `${get(brandStyles, ['typography', 'padding', 'top'], 0)} 0 0 `,

          fontSize: get(brandStyles, ['typography', 'desktop', 'fontSizeBody1']),

          '&.Mui-selected': {
            backgroundColor: '#207028 !important',
            border: 'medium none',
            color: '#fff',
            fontWeight: 'bold',

            '&:focus': {
              backgroundColor: '#43aa4c',
            },
          },
        },
        today: {
          fontWeight: 'bold',
          '&:not(.Mui-selected)': {
            border: 'none',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: brandStyles.colors.palette.background.paper,
          fontSize: 15,
        },
        inputMultiline: {
          padding: '0px !important',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {},
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent !important',
          borderRadius: 0,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: 14,
          marginLeft: '0px !important',
          lineHeight: '18px',
          color: '#000 !important',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: brandStyles.colors.palette.fontColor.body,
          fontSize: brandStyles.typography.desktop.fontSizeBody1,
          lineHeight: 'initial !important',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          [baseTheme.breakpoints.up('lg')]: {
            alignSelf: 'center',
          },
        },
        regular: {
          [baseTheme.breakpoints.up('md')]: {
            minHeight: '96px !important',
          },
          [baseTheme.breakpoints.up('lg')]: {
            minHeight: '96px !important',
            width: '100%',
          },
        },
        gutters: {
          paddingLeft: '32px !important',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: 'rgba(0,0,0,0.23) !important',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        button: {
          '&:hover': {
            backgroundColor: brandStyles.colors.button.outlined.hover.backgroundColor,
          },
          '&.Mui-selected': {
            backgroundColor: brandStyles.colors.button.selected.backgroundColor,
            color: brandStyles.colors.button.selected.color,

            '&:hover': {
              backgroundColor: brandStyles.colors.button.selected.hover.backgroundColor,
              color: brandStyles.colors.button.selected.hover.color,
            },
          },
        },
      },
    },
    MuiPickersBasePicker: {
      styleOverrides: {
        container: {
          alignItems: 'center',
        },
        pickerView: {
          overflow: 'auto',
        },
      },
    },
  },
});

export default theme;
